var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent, setUserId } from "firebase/analytics";
import { CategoryTrackingElement, isPerkClickHit, PageName, PerkSource, PerkTrackingElement, Source, TrackingAction, TrackingElement, TrackingId, SortType, WebExtensionElement, Country } from 'lib/workperks/models/Analytics';
import { FIREBASE_CONFIG } from 'lib/workperks/common/constants';
import { detect } from 'detect-browser';
import LibSite from 'lib/Site';
import { VenngoRedemptionType } from "lib/workperks/models/RedemptionOptions";
export const getBrowserInfo = detect();
/**
 * This service's state is always set by `analyticsMiddleware`
 *
 * {@link analyticsMiddleware}
 */
class AnalyticsService {
    constructor() {
        this.TYPE_ID = 37;
        this.debug = true;
        this.firebaseApp = null;
        this.track = (id, params) => {
            if (!this.isInitialized)
                return console.warn('AnalyticsService -- Not initialized. Skipping tracking.');
            params = this.mapParameters(params);
            const event = Object.assign({ key_id: id, type_id: this.TYPE_ID, site_id: this.site.id, user_ID: this.user.id, page_name: PageName[this.pageName], location_type: this.locationType, location_latitude: this.location.latitude, location_longitude: this.location.longitude, location_country: this.location.country }, params);
            if (this.templateId !== null && this.templateId !== undefined) {
                event['promotion_id'] = this.templateId;
            }
            if (!event["source"]) {
                event["source"] = this.getBrowserType();
            }
            Object.keys(event).forEach(key => {
                if (event[key] === null) {
                    delete event[key];
                }
            });
            const analytics = getAnalytics(this.firebaseApp);
            setUserId(analytics, (this.user.id).toString());
            logEvent(analytics, TrackingId[id], event);
            if (this.debug) {
                console.log(JSON.stringify(Object.assign(Object.assign({ type: 'AnalyticsHit', event: TrackingId[id] }, event))));
            }
            return null;
        };
        this.trackRegistration = (id, params) => __awaiter(this, void 0, void 0, function* () {
            const pageName = params.page_name;
            const event = Object.assign({ key_id: id, type_id: this.TYPE_ID, page: PageName[pageName], source: this.getBrowserType() }, params);
            const analytics = getAnalytics(this.firebaseApp);
            // @ts-expect-error - Expected error
            if (params.user_id) {
                setUserId(analytics, (this.user.id).toString());
            }
            logEvent(analytics, TrackingId[id], event);
            if (this.debugMode) {
                console.log(JSON.stringify(Object.assign(Object.assign({ type: 'AnalyticsHit', event: TrackingId[id] }, event))));
            }
        });
        this.initialize = (site, user, location) => {
            var _a;
            if (site) {
                this.setSite(site);
            }
            if (user) {
                this.setUser(user);
            }
            if (location) {
                this.setLocation(location);
            }
            if (!this.pageName) {
                const pageIndex = window.s.pageName;
                this.pageName = (_a = PageName[pageIndex]) !== null && _a !== void 0 ? _a : null;
            }
            this.firebaseApp = initializeApp(FIREBASE_CONFIG);
            this.setDebugMode(LibSite.getCurrentSite().isTrackingLogEnabled);
            return this;
        };
        this.mapParameters = (params) => {
            if (isPerkClickHit(params) && !params.campaign_id)
                params.campaign_id = params.perk_id;
            return params;
        };
        this.trackBannerHit = (action = TrackingAction.Loaded, perk, index = 0, elementName = PerkTrackingElement.Banner) => {
            var _a, _b, _c, _d;
            const trackingId = TrackingId.PerkInteraction;
            if (!trackingId) {
                console.warn(`AnalyticsService@trackBannerHitByRoute: Could not find tracking ID for route '${this.pageName}'`);
                if (this.debugMode)
                    throw Error(`Tracking ID is not mapped for route '${this.pageName}'`);
                return;
            }
            const payload = {
                perk_id: perk.id,
                account_id: (_a = perk.accountId) !== null && _a !== void 0 ? _a : null,
                position: Number(index),
                element_name: elementName,
                perk_source: this.getObjectSource(perk),
                action,
                campaign_id: (_b = perk.campaign_id) !== null && _b !== void 0 ? _b : null
            };
            if (perk.searchQuery)
                payload.keyword = perk.searchQuery;
            if ((_c = perk.category) === null || _c === void 0 ? void 0 : _c.id)
                payload.category_id = Number(perk.category.id);
            if ((_d = perk.subcategory) === null || _d === void 0 ? void 0 : _d.id)
                payload.subcategory_id = Number(perk.subcategory.id);
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            if (perk.promotion)
                payload.promotion_id = perk.promotion.id;
            this.track(trackingId, payload);
        };
        this.trackPageView = (trackingPayload) => {
            const trackingId = TrackingId.PageView;
            let payload = {};
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            if (trackingPayload) {
                payload = {
                    popup_id: trackingPayload["popup_id"] ? parseInt(trackingPayload["popup_id"]) : null,
                    promotion_id: trackingPayload["promotion_id"] ? parseInt(trackingPayload["promotion_id"]) : null,
                    source: parseInt(trackingPayload["wbeId"]) ? Source.WebBrowserExtension : null,
                    tag_id: trackingPayload["tag"] ? parseInt(trackingPayload["tag"]) : null,
                    category_id: trackingPayload["category_id"] ? parseInt(trackingPayload["category_id"]) : null,
                    subcategory_id: trackingPayload["subcategory_id"] ? parseInt(trackingPayload["subcategory_id"]) : null,
                    perk_id: trackingPayload["perk_id"] ? parseInt(trackingPayload["perk_id"]) : null,
                    campaign_id: trackingPayload["campaign_id"] ? parseInt(trackingPayload["campaign_id"]) : null,
                    account_id: trackingPayload["account_id"] ? parseInt(trackingPayload["account_id"]) : null,
                    perk_source: trackingPayload["perk_source"] ? trackingPayload["perk_source"] : null,
                    edition_id: (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["edition"]) ? parseInt(trackingPayload["edition"]) : null,
                    alert_banner_id: (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["alert_banner_id"]) ? parseInt(trackingPayload["alert_banner_id"]) : null,
                    spinwheel_id: (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["spinwheel_id"]) ? parseInt(trackingPayload["spinwheel_id"]) : null,
                    keyword: trackingPayload["keyword"] ? trackingPayload["keyword"] : null,
                    position: trackingPayload["position"] ? parseInt(trackingPayload["position"]) : null,
                };
                if (trackingPayload["notification_id"]) {
                    payload.notification_id = (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["notification_id"]) ? parseInt(trackingPayload["notification_id"]) : null;
                    payload.notification_status = (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["notification_status"]) ? trackingPayload["notification_status"] : null;
                    payload.notification_type = (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["notification_type"]) ? trackingPayload["notification_type"] : null;
                    payload.promotion_id = (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["promotion_id"]) ? parseInt(trackingPayload["promotion_id"]) : null;
                }
                if (trackingPayload["notification_id"]) {
                    payload.notification_id = (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["notification_id"]) ? parseInt(trackingPayload["notification_id"]) : null;
                    payload.notification_status = (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["notification_status"]) ? trackingPayload["notification_status"] : null;
                    payload.notification_type = (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["notification_type"]) ? trackingPayload["notification_type"] : null;
                    payload.promotion_id = (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["promotion_id"]) ? parseInt(trackingPayload["promotion_id"]) : null;
                }
                if (trackingPayload["keyword"] && this.pageName != PageName.Search) {
                    payload.sponsored_keyword = (this.location.country !== Country.US && trackingPayload["sponsored_keyword"]) ? parseInt(trackingPayload["sponsored_keyword"]) : 0;
                    payload.sponsored_result = (this.location.country !== Country.US && trackingPayload["sponsored_result"]) ? parseInt(trackingPayload["sponsored_result"]) : 0;
                }
            }
            this.track(trackingId, payload);
            return null;
        };
        this.trackWebExtensionHit = (trackingPayload) => {
            const trackingId = TrackingId.PerkInteraction;
            if (!trackingPayload) {
                throw Error("Tracking Payload is required.");
            }
            const wbeId = parseInt(trackingPayload["wbeId"]);
            if (!(wbeId === TrackingId.WBEPopupClick || wbeId === TrackingId.WBEPageNotificationClick)) {
                throw Error("wbeId Parameter is incorrect.");
            }
            const payload = {
                promotion_id: trackingPayload["promotion_id"] ? parseInt(trackingPayload["promotion_id"]) : null,
                keyword: trackingPayload["keyword"] ? trackingPayload["keyword"] : null,
                category_id: trackingPayload["category_id"] ? parseInt(trackingPayload["category_id"]) : null,
                subcategory_id: trackingPayload["subcategory_id"] ? parseInt(trackingPayload["subcategory_id"]) : null,
                perk_id: trackingPayload["perk_id"] ? parseInt(trackingPayload["perk_id"]) : null,
                campaign_id: trackingPayload["campaign_id"] ? parseInt(trackingPayload["campaign_id"]) : null,
                account_id: trackingPayload["account_id"] ? parseInt(trackingPayload["account_id"]) : null,
                source: Source.WebBrowserExtension,
                action: TrackingAction.Click,
                element_name: wbeId === TrackingId.WBEPopupClick ? WebExtensionElement.WBEPopupClick : WebExtensionElement.WBEPageNotificationClick,
                perk_source: trackingPayload["perk_source"] ? trackingPayload["perk_source"] : null
            };
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
            return null;
        };
        // TODO: Verify firebase automatically tracks newsletter or we need to keep tracking it in the code
        this.trackNewsletterHit = (trackingPayload, perk) => {
            var _a, _b;
            const trackingId = TrackingId.PerkInteraction;
            const payload = {
                perk_id: perk.id,
                account_id: (_a = perk.accountId) !== null && _a !== void 0 ? _a : null,
                campaign_id: (_b = perk.campaign_id) !== null && _b !== void 0 ? _b : null,
                position: (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["position"]) ? parseInt(trackingPayload["position"]) : 1,
                category_id: isNaN(Number(perk.category.id)) ? null : Number(perk.category.id),
                subcategory_id: isNaN(Number(perk.subcategory.id)) ? null : Number(perk.subcategory.id),
                perk_source: this.getObjectSource(perk),
                source: Source.Newsletter,
                action: TrackingAction.Click,
                edition_id: (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["edition"]) ? parseInt(trackingPayload["edition"]) : null,
                element_name: (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["element"]) ? trackingPayload["element"] : null,
                newsletter_type: (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["type"]) ? trackingPayload["type"] : null,
                promotion_id: trackingPayload["promotion_id"] ? parseInt(trackingPayload["promotion_id"]) : null,
            };
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
            return null;
        };
        this.trackReferralLinkClick = (perk) => {
            var _a;
            const trackingId = TrackingId.ReferralLinks;
            const payload = {
                perk_id: perk.id,
                account_id: (_a = perk.accountId) !== null && _a !== void 0 ? _a : null,
                category_id: isNaN(Number(perk.category.id)) ? null : Number(perk.category.id),
                subcategory_id: isNaN(Number(perk.subcategory.id)) ? null : Number(perk.subcategory.id),
                perk_source: this.getObjectSource(perk),
                action: TrackingAction.Click
            };
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
            return null;
        };
        this.trackCategoryClick = (categoryId, elementName = CategoryTrackingElement.Menu) => {
            const trackingId = TrackingId.CategoryClick;
            const payload = {
                category_id: categoryId,
                element_name: elementName
            };
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
            return null;
        };
        this.trackViewAllClick = (action = TrackingAction.Click, elementName = TrackingElement.PromotionViewAll, tracking = TrackingId.PromotionViewAll, source) => {
            const trackingId = tracking;
            const payload = {
                action,
                element_name: elementName,
                perk_source: source
            };
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
            return null;
        };
        this.trackPagination = (pageStartIndex) => {
            const trackingId = TrackingId.Pagination;
            const payload = {
                element_name: TrackingElement.Pagination,
                position: pageStartIndex
            };
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
            return null;
        };
        this.trackPerkSearch = (perk, action = TrackingAction.Loaded) => {
            var _a, _b, _c, _d, _e, _f;
            const trackingId = TrackingId.PerkInteraction;
            const payload = {
                perk_id: perk.id,
                account_id: (_a = perk.accountId) !== null && _a !== void 0 ? _a : null,
                element_name: 'ShortSearch',
                category_id: isNaN(Number(perk.category.id)) ? null : Number(perk.category.id),
                subcategory_id: isNaN(Number(perk.subcategory.id)) ? null : Number(perk.subcategory.id),
                perk_source: this.getObjectSource(perk),
                action,
                campaign_id: (_b = perk.campaign_id) !== null && _b !== void 0 ? _b : null,
                promotion_id: ((_c = perk.promotion) === null || _c === void 0 ? void 0 : _c.id) ? perk.promotion.id : null,
                keyword: (_e = (_d = perk.keyword) !== null && _d !== void 0 ? _d : perk.searchQuery) !== null && _e !== void 0 ? _e : null,
                position: (_f = perk.position) !== null && _f !== void 0 ? _f : null,
                sponsored_keyword: (this.location.country !== Country.US && perk.sponsored_keyword) ? perk.sponsored_keyword : 0,
                sponsored_result: (this.location.country !== Country.US && perk.sponsored_result) ? perk.sponsored_result : 0,
            };
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
            return null;
        };
        this.trackSearchQuery = (keyword, source, keywordSource, distance, filters = null) => {
            const payload = {
                keyword,
                action: TrackingAction.Submit,
                source: 'web',
                keyword_source: keywordSource,
                distance,
                perk_source: source,
            };
            if (filters) {
                // @ts-expect-error - Expected error
                payload['expiring_filter'] = !!filters.showExpiringOnly;
                // @ts-expect-error - Expected error
                payload['new_filter'] = !!filters.showNewOnly;
                // @ts-expect-error - Expected error
                payload['instore_filter'] = filters.redemptionFilter.includes('instore');
                // @ts-expect-error - Expected error
                payload['online_filter'] = filters.redemptionFilter.includes('online');
                // @ts-expect-error - Expected error
                payload['mobile_filter'] = filters.redemptionFilter.includes('mobile');
                // @ts-expect-error - Expected error
                payload['email_filter'] = filters.redemptionFilter.includes('email');
                // @ts-expect-error - Expected error
                payload['sort_type'] = filters.sort !== 0 ? SortType[filters.sort] : null;
            }
            return this.track(TrackingId.SearchSubmission, payload);
        };
        this.trackAccountCard = (account, action = TrackingAction.Loaded) => {
            var _a;
            const trackingId = TrackingId.AccountInteraction;
            const payload = {
                account_id: (_a = account.id) !== null && _a !== void 0 ? _a : null,
                action,
                element_name: 'AccountCard',
                source: 'web',
                account_source: account.source
            };
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
            return null;
        };
        this.trackPerkCardImpression = (action = TrackingAction.Loaded, perk, index = 1, elementName = PerkTrackingElement.PerkCard) => {
            var _a, _b, _c, _d;
            if (!perk || !perk.id) {
                return;
            }
            const trackingId = TrackingId.PerkInteraction;
            //
            const payload = {
                perk_id: perk.id,
                account_id: (_a = perk.accountId) !== null && _a !== void 0 ? _a : null,
                campaign_id: (_b = perk.campaign_id) !== null && _b !== void 0 ? _b : null,
                element_name: elementName,
                category_id: isNaN(Number(perk.category.id)) ? null : Number(perk.category.id),
                subcategory_id: isNaN(Number(perk.subcategory.id)) ? null : Number(perk.subcategory.id),
                perk_source: this.getObjectSource(perk),
                keyword: (_d = (_c = perk.keyword) !== null && _c !== void 0 ? _c : perk.searchQuery) !== null && _d !== void 0 ? _d : null,
                position: perk.index ? perk.index : Number(index) + 1,
                action
            };
            if (perk.searchQuery) {
                payload.sponsored_keyword = (this.location.country !== Country.US && perk.sponsored_keyword) ? perk.sponsored_keyword : 0;
                payload.sponsored_result = (this.location.country !== Country.US && perk.sponsored_result) ? perk.sponsored_result : 0;
            }
            if (perk.promotion)
                payload.promotion_id = perk.promotion.id;
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
            return null;
        };
        this.trackPopupImpression = (action = TrackingAction.Loaded, popup, meta, element_name = TrackingElement.Popup, editionId) => {
            var _a;
            if (!(popup === null || popup === void 0 ? void 0 : popup.id)) {
                return;
            }
            const trackingId = TrackingId.PopUpInteraction;
            const promotionId = [1, 2, 5].includes(Number((_a = popup.type) === null || _a === void 0 ? void 0 : _a.id)) ? meta.promotion_id : null;
            const payload = {
                popup_id: popup.id,
                action,
                element_name,
                perk_id: meta.campaign ? meta.campaign.perk_id : null,
                account_id: meta.campaign ? meta.campaign.account_id : null,
                campaign_id: meta.campaign ? meta.campaign.campaign_id : null,
                category_id: meta.campaign ? meta.campaign.category_id : null,
                subcategory_id: meta.campaign ? meta.campaign.subcategory_id : null,
                promotion_id: promotionId,
                perk_source: meta.campaign ? meta.campaign.source : null,
                spinwheel_id: meta.spinwheel_id ? meta.spinwheel_id : null,
                edition_id: editionId ? Number(editionId) : null,
                template_type: popup.template ? Number(popup.template.id) : null,
            };
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
            return null;
        };
        this.trackAlertBannerImpression = (action = TrackingAction.Loaded, data, meta, element_name = TrackingElement.AlertBanner, editionId, emailId) => {
            if (!data || !data.id) {
                return;
            }
            const trackingId = TrackingId.AlertBannerInteraction;
            const promotionId = meta.promotion_id ? meta.promotion_id : null;
            const payload = {
                alert_banner_id: data.id,
                action,
                element_name,
                perk_id: meta.campaign ? meta.campaign.perk_id : null,
                account_id: meta.campaign ? meta.campaign.account_id : null,
                campaign_id: meta.campaign ? meta.campaign.campaign_id : null,
                category_id: meta.campaign ? meta.campaign.category_id : null,
                subcategory_id: meta.campaign ? meta.campaign.subcategory_id : null,
                promotion_id: promotionId,
                perk_source: meta.campaign ? meta.campaign.source : null,
                edition_id: editionId ? Number(editionId) : null,
                template_type: data.template.id,
                email_id: emailId ? Number(emailId) : null,
            };
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
            return null;
        };
        this.trackUserRegistration = (action = TrackingAction.Loaded, register, step = 1, pageName, elementName = TrackingElement.UserAccountRegistration) => {
            var _a, _b, _c, _d;
            const trackingId = TrackingId.UserRegistrationInteraction;
            const payload = {
                // @ts-expect-error - Expected error
                site_id: (_a = register.site_id) !== null && _a !== void 0 ? _a : null,
                // @ts-expect-error - Expected error
                user_ID: (_b = register.user_id) !== null && _b !== void 0 ? _b : null,
                // @ts-expect-error - Expected error
                family_invited: (_c = register.family_invited) !== null && _c !== void 0 ? _c : false,
                // @ts-expect-error - Expected error
                email: (_d = register.email) !== null && _d !== void 0 ? _d : null,
                step,
                action,
                element_name: elementName,
                page_name: pageName,
            };
            return this.trackRegistration(trackingId, payload);
        };
        this.trackAccountImpression = (action = TrackingAction.Loaded, account, index = 0, elementName = PerkTrackingElement.TopBrand) => {
            var _a;
            const trackingId = TrackingId.AccountInteraction;
            const payload = {
                account_id: account.id,
                position: Number(index) + 1,
                element_name: elementName,
                category_id: ((_a = window.s.search) === null || _a === void 0 ? void 0 : _a.selectedParentCategory) ? Number(window.s.search.selectedParentCategory.id) : null,
                account_source: this.getObjectSource(account), // add ad or es in top brands
                action,
                source: Source.Web
            };
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
            return null;
        };
        this.getObjectSource = (object) => {
            if (!object.licenses)
                return PerkSource.VenngoCA;
            if (object.licenses.includes(2)) {
                return PerkSource.VenngoUS;
            }
            else if (object.licenses.includes(3)) {
                return PerkSource.AccessUS;
            }
            else {
                return PerkSource.VenngoCA;
            }
        };
        this.getBrowserType = () => {
            if (window.s.isMobile) {
                // This if block is for the web views that opens in the mobile app
                return Source.Mobile;
            }
            else if (/Android OS|iOS/i.test(getBrowserInfo.os) || window.s.redirectMobile) {
                return Source.MobileWeb;
            }
            return Source.Web;
        };
        this.trackNotificationImpression = (action = TrackingAction.Loaded, notification, element_name = TrackingElement.NotificationPanel) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
            const trackingId = TrackingId.NotificationInteraction;
            const payload = {
                notification_id: (_a = notification.id) !== null && _a !== void 0 ? _a : null,
                action,
                element_name,
                notification_status: String((_b = notification.status) === null || _b === void 0 ? void 0 : _b.id) || null,
                source: this.getBrowserType(),
                notification_type: String((_c = notification.type) === null || _c === void 0 ? void 0 : _c.id) || null,
                // meta
                perk_id: ((_d = notification.meta) === null || _d === void 0 ? void 0 : _d.campaign) ? notification.meta.campaign.perk_id : null,
                account_id: ((_e = notification.meta) === null || _e === void 0 ? void 0 : _e.campaign) ? notification.meta.campaign.account_id : null,
                campaign_id: ((_f = notification.meta) === null || _f === void 0 ? void 0 : _f.campaign) ? notification.meta.campaign.campaign_id : null,
                category_id: ((_g = notification.meta) === null || _g === void 0 ? void 0 : _g.campaign) ? notification.meta.campaign.category_id : null,
                subcategory_id: ((_h = notification.meta) === null || _h === void 0 ? void 0 : _h.campaign) ? notification.meta.campaign.subcategory_id : null,
                promotion_id: ((_j = notification.meta) === null || _j === void 0 ? void 0 : _j.promotion_id) || null,
                perk_source: ((_k = notification.meta) === null || _k === void 0 ? void 0 : _k.campaign) ? notification.meta.campaign.source : null,
            };
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
            return null;
        };
        this.trackPerkRedemption = (perk, redemptionType, trackingPayload) => {
            var _a;
            const trackingId = TrackingId.Redemption;
            if (!trackingId) {
                console.warn(`AnalyticsService@trackPerkRedemption: Could not find tracking ID for route '${this.pageName}'`);
                if (this.debugMode)
                    throw Error(`Tracking ID is not mapped for route '${this.pageName}'`);
                return;
            }
            const payload = {
                perk_id: perk.id, // Perk ID
                campaign_id: perk.campaign_id, // Campaign ID
                account_id: perk.accountId, // Account ID
                // @ts-expect-error - Expected error
                redemption_type: VenngoRedemptionType[redemptionType], // Specifies the type of redemption that occurred.
                perk_source: this.getObjectSource(perk),
                category_id: isNaN(Number(perk.category.id)) ? null : Number(perk.category.id),
                subcategory_id: isNaN(Number(perk.subcategory.id)) ? null : Number(perk.subcategory.id),
                promotion_id: (_a = perk.promotionId) !== null && _a !== void 0 ? _a : null,
                edition_id: (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["edition"]) ? parseInt(trackingPayload["edition"]) : null,
                keyword: trackingPayload["keyword"] ? trackingPayload["keyword"] : null,
                position: trackingPayload["position"] ? parseInt(trackingPayload["position"]) : null,
                alert_banner_id: (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["alert_banner_id"]) ? parseInt(trackingPayload["alert_banner_id"]) : null,
            };
            if (trackingPayload["keyword"]) {
                payload.sponsored_keyword = (this.location.country !== Country.US && trackingPayload["sponsored_keyword"]) ? parseInt(trackingPayload["sponsored_keyword"]) : 0;
                payload.sponsored_result = (this.location.country !== Country.US && trackingPayload["sponsored_result"]) ? parseInt(trackingPayload["sponsored_result"]) : 0;
            }
            if (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["popup_id"]) {
                payload.popup_id = (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["popup_id"]) ? parseInt(trackingPayload["popup_id"]) : null;
            }
            if (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["notification_id"]) {
                payload.notification_id = (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["notification_id"]) ? parseInt(trackingPayload["notification_id"]) : null;
                payload.notification_status = (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["notification_status"]) ? trackingPayload["notification_status"] : null;
                payload.notification_type = (trackingPayload === null || trackingPayload === void 0 ? void 0 : trackingPayload["notification_type"]) ? trackingPayload["notification_type"] : null;
            }
            if (!this.pageName)
                this.setPageName(window.s.pageName);
            this.track(trackingId, payload);
        };
    }
    setUser(user) {
        this.user = user;
        return this;
    }
    setSite(site) {
        this.site = site;
        return this;
    }
    setLocation(location) {
        this.location = location;
        return this;
    }
    setDebugMode(value) {
        this.debug = value;
        return this;
    }
    setPageName(value) {
        this.pageName = PageName[value];
        return this;
    }
    setTemplateId(value) {
        this.templateId = value !== null && value !== void 0 ? value : null;
        return this;
    }
    get isInitialized() {
        return this.site !== undefined && this.user !== undefined;
    }
    get debugMode() {
        return this.site !== undefined;
    }
    get locationType() {
        return this.location.id === 0 ? 'GPS' : this.location.id ? 'Saved' : 'Temporary';
    }
}
const analyticsService = new AnalyticsService();
export default analyticsService;
